"use client";

import type { GoogleCalendarConnectionStatus } from "@/types";

import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useState } from "react";

import { SettingsCard } from "./SettingsCard.tsx";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface GoogleCalendarCardProps {
  status: GoogleCalendarConnectionStatus;
  isLoading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onReset: () => void;
  onReauth: () => void;
  isResetting?: boolean;
}

export function GoogleCalendarCard({
  status,
  isLoading,
  onConnect,
  onDisconnect,
  onReset,
  onReauth,
  isResetting = false,
}: GoogleCalendarCardProps) {
  const { t } = useTranslation(["calendar", "common"]);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const renderActions = () => {
    if (status === "not_connected") {
      return (
        <Button
          className="gap-1.5"
          disabled={isLoading || isResetting}
          size="sm"
          variant="foreground"
          onClick={onConnect}
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-1">
              <Icon className="animate-spin" height={"1rem"} icon="mdi:loading" width={"1rem"} />
            </div>
          ) : (
            <div className="flex items-center justify-center p-1">
              <Icon className="" height={"1rem"} icon="mdi:google" width={"1rem"} />
            </div>
          )}
          {t("calendar:settings.googleCalendar.connect")}
        </Button>
      );
    }

    if (status === "connected_healthy") {
      return (
        <Button
          className="gap-1.5"
          disabled={isLoading || isResetting}
          size="sm"
          variant="foreground"
          onClick={onDisconnect}
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-1">
              <Icon className="animate-spin" height={"1rem"} icon="mdi:loading" width={"1rem"} />
            </div>
          ) : (
            <div className="flex items-center justify-center p-1">
              <Icon className="" height={"1rem"} icon="mdi:link-off" width={"1rem"} />
            </div>
          )}
          {t("calendar:settings.googleCalendar.disconnect")}
        </Button>
      );
    }

    if (status === "requires_reauth") {
      return (
        <div className="flex gap-2">
          <Button
            className="gap-1.5"
            disabled={isLoading || isResetting}
            size="sm"
            variant="outline"
            onClick={onDisconnect}
          >
            {isLoading ? (
              <div className="flex items-center justify-center p-1">
                <Icon className="animate-spin" height={"1rem"} icon="mdi:loading" width={"1rem"} />
              </div>
            ) : (
              <div className="flex items-center justify-center p-1">
                <Icon className="" height={"1rem"} icon="mdi:link-off" width={"1rem"} />
              </div>
            )}
            {t("calendar:settings.googleCalendar.disconnect")}
          </Button>
          <Button
            className="gap-1.5"
            disabled={isLoading || isResetting}
            size="sm"
            variant="foreground"
            onClick={onReauth}
          >
            {isLoading ? (
              <div className="flex items-center justify-center p-1">
                <Icon className="animate-spin" height={"1rem"} icon="mdi:loading" width={"1rem"} />
              </div>
            ) : (
              <div className="flex items-center justify-center p-1">
                <Icon className="" height={"1rem"} icon="mdi:refresh" width={"1rem"} />
              </div>
            )}
            {t("calendar:settings.googleCalendar.reauth")}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <SettingsCard
        action={renderActions()}
        bottomCircle={"from-yellow-500 to-red-700"}
        description={t("calendar:settings.googleCalendar.description")}
        icon="logos:google-calendar"
        title={t("calendar:settings.googleCalendar.title")}
        topCircle={"from-blue-600 to-green-500"}
      >
        <div className="bg-muted flex items-center gap-2 rounded-md p-3">
          <div
            className={`h-2.5 w-2.5 rounded-full ${
              status === "connected_healthy"
                ? "bg-green-500"
                : status === "requires_reauth"
                  ? "bg-amber-500"
                  : "bg-red-500"
            }`}
          />
          <span className="text-accent-foreground text-sm">
            {status === "connected_healthy"
              ? t("calendar:settings.googleCalendar.connected")
              : status === "requires_reauth"
                ? t("calendar:settings.googleCalendar.requiresReauth")
                : t("calendar:settings.googleCalendar.notConnected")}
          </span>
        </div>
      </SettingsCard>

      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("calendar:settings.googleCalendar.resetDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("calendar:settings.googleCalendar.resetDialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-muted mt-4 rounded-md p-3 text-sm">
            <p className="text-muted-foreground">
              {t("calendar:settings.googleCalendar.manualRemovalNote")}{" "}
              <a
                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                href="https://myaccount.google.com/permissions"
                rel="noopener noreferrer"
                target="_blank"
              >
                {t("calendar:settings.googleCalendar.googleAccountSettings")}
              </a>
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsResetDialogOpen(false)}>
              {t("common:cancel")}
            </Button>
            <Button
              disabled={isResetting}
              variant="destructive"
              onClick={() => {
                onReset();
                setIsResetDialogOpen(false);
              }}
            >
              {isResetting ? (
                <Icon
                  className="mr-2 animate-spin"
                  height={"1rem"}
                  icon="mdi:loading"
                  width={"1rem"}
                />
              ) : null}
              {t("calendar:settings.googleCalendar.confirmReset")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
